<script setup>
import { computed, ref, watch } from "vue";
import moment from "moment";
import { storeToRefs } from "pinia";
import useStore from "../store";
import { bookingItemsAreTooClose, isEnabledFlag } from "../helpers";
import {
  attractionFromProductCode,
  attractionProductCodes,
} from "../helpers/attractions";
import AlertError from "./AlertError.vue";
import BookingMovePayment from "./BookingMovePayment.vue";
import Session from "./Session.vue";
import TicketIcon from "./TicketIcon.vue";

const props = defineProps({
  item: {
    type: Object,
    default: null,
  },
  itemInd: {
    type: [String, Number],
    default: null,
  },
  booking: {
    type: Object,
    default: null,
  },
  bookingHasPastItems: {
    type: Boolean,
    default: false,
  },
  quote: {
    type: Object,
    default: null,
  },
  quoting: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["close", "quote", "confirm", "reset-quote"]);
const store = useStore();
const { attractions, options } = storeToRefs(store);
const selectedAttractionId = ref(null);
const selectedProductCode = ref("");
const selectedTime = ref("");
const selectedAvailability = ref({});
const selectedPrices = ref([]);
const payment = ref(null);

const normalizeLabel = (label) => String(label ?? "").trim().toLowerCase();

const sourceAttraction = computed(() =>
  attractionFromProductCode(attractions.value, props.item?.productCode)
);

const availableAttractions = computed(() =>
  attractions.value.filter((attraction) => {
    const enabledRetail = attraction?.enabled_retail;
    const isRetailEnabled = enabledRetail === undefined || enabledRetail === null
      ? true
      : isEnabledFlag(enabledRetail);

    return isRetailEnabled && attractionProductCodes(attraction).length > 0;
  })
);

const selectedAttraction = computed(() =>
  availableAttractions.value.find(
    (attraction) => Number(attraction.id) === Number(selectedAttractionId.value)
  ) ?? null
);

const productOptions = computed(() => {
  const configuredOptions = selectedAttraction.value?.rezdy_product_options ?? [];

  if (Array.isArray(configuredOptions) && configuredOptions.length > 0) {
    return configuredOptions.filter((option) => option?.product);
  }

  return selectedAttraction.value?.rezdy_retail_product_code
    ? [{
        product: selectedAttraction.value.rezdy_retail_product_code,
        title: selectedAttraction.value.retail_title ?? selectedAttraction.value.title,
        desc: "",
      }]
    : [];
});

const currentTime = computed(() => {
  const time = props.item?.startTimeLocal ?? props.item?.startTime ?? false;
  return time ? moment(time).format("dddd Do MMMM YYYY, h:mma") : "";
});

const selectedTimeLabel = computed(() =>
  selectedTime.value ? moment(selectedTime.value).format("dddd Do MMMM YYYY, h:mma") : ""
);

const moveBookingCutoffMinutes = computed(() =>
  Number(sourceAttraction.value?.move_booking_cutoff_minutes ?? 0)
);

const moveBookingsEnabled = computed(() =>
  isEnabledFlag(sourceAttraction.value?.enabled_move_bookings)
);

const bookingIsAffectedByClosureEvent = computed(() =>
  Boolean(props.booking?.is_closure_event_booking || props.booking?.closure_event_booking?.active)
);

const bookingIsWithinMoveCutoff = computed(() => {
  if (bookingIsAffectedByClosureEvent.value) {
    return false;
  }

  // const time = props.item?.startTimeLocal ?? props.item?.startTime ?? null;
  const time = props.item?.startTime ?? null;

  if (props.item?.productCode === "P5BLY8") {
    const moveCutoff = moment(time)
      .startOf("day")
      .subtract(1, "day")
      .hour(17)
      .minute(0)
      .second(0)
      .millisecond(0);

    return moment().isSameOrAfter(moveCutoff);
  }

  if (!time || moveBookingCutoffMinutes.value <= 0) {
    return false;
  }

  return moment(time).diff(moment(), "minutes", true) <= moveBookingCutoffMinutes.value;
});

const quantities = computed(() => props.item?.quantities ?? []);
const totalParticipants = computed(() =>
  quantities.value.reduce((total, quantity) => total + Number(quantity?.value ?? 0), 0)
);
const currentPriceTotal = computed(() =>
  quantities.value.reduce(
    (total, quantity) => total + Number(quantity?.value ?? 0) * Number(quantity?.optionPrice ?? 0),
    0
  )
);
const selectedPriceTotal = computed(() =>
  quantities.value.reduce((total, quantity) => {
    const option = (selectedAvailability.value?.priceOptions ?? []).find(
      (priceOption) => normalizeLabel(priceOption?.label) === normalizeLabel(quantity?.optionLabel)
    );

    return total + Number(quantity?.value ?? 0) * Number(option?.price ?? 0);
  }, 0)
);

const sourceAttractionTitle = computed(() =>
  sourceAttraction.value?.retail_title ?? props.item?.productName ?? "Booking"
);

const selectedAttractionTitle = computed(() =>
  selectedAttraction.value?.retail_title
    ?? selectedAttraction.value?.title
    ?? sourceAttractionTitle.value
);

const selectionChanged = computed(() => {
  const current = props.item?.startTimeLocal ?? props.item?.startTime ?? "";
  const timeChanged = !!selectedTime.value && !moment(selectedTime.value).isSame(moment(current));

  return timeChanged || selectedProductCode.value !== props.item?.productCode;
});

const summaryUsesSelection = computed(() => !!props.quote || selectionChanged.value);

const summaryTitle = computed(() =>
  summaryUsesSelection.value ? selectedAttractionTitle.value : sourceAttractionTitle.value
);

const summaryTime = computed(() => {
  if (!summaryUsesSelection.value) {
    return currentTime.value;
  }

  return selectedTimeLabel.value || "Choose a new date and time";
});

const summaryTotal = computed(() => {
  if (props.quote) {
    return Number(props.quote.new_total ?? selectedPriceTotal.value);
  }

  if (summaryUsesSelection.value && !selectedAvailability.value?.startTimeLocal) {
    return null;
  }

  return summaryUsesSelection.value ? selectedPriceTotal.value : currentPriceTotal.value;
});

const summaryQuantities = computed(() => quantities.value.map((quantity) => {
  const selectedPriceOption = (selectedAvailability.value?.priceOptions ?? []).find(
    (priceOption) => normalizeLabel(priceOption?.label) === normalizeLabel(quantity?.optionLabel)
  );
  const useSelectedPrice = summaryUsesSelection.value && selectedPriceOption;

  return {
    ...quantity,
    total: useSelectedPrice
      ? Number(quantity?.value ?? 0) * Number(selectedPriceOption?.price ?? 0)
      : Number(quantity?.value ?? 0) * Number(quantity?.optionPrice ?? 0),
  };
}));

const bookingHasDistanceConflict = computed(() => {
  if (!selectedTime.value || props.itemInd === null || props.itemInd === undefined) {
    return false;
  }

  const items = (props.booking?.items ?? []).map((currentItem, index) => ({
    ...currentItem,
    attraction: index === Number(props.itemInd)
      ? selectedAttraction.value?.id
      : attractionFromProductCode(attractions.value, currentItem?.productCode)?.id,
    productCode: index === Number(props.itemInd)
      ? selectedProductCode.value
      : currentItem?.productCode,
    time: index === Number(props.itemInd)
      ? selectedTime.value
      : currentItem?.startTimeLocal ?? currentItem?.startTime ?? null,
  }));

  return bookingItemsAreTooClose(items);
});

const seatsRequired = computed(() => {
  if (!Array.isArray(selectedAvailability.value?.priceOptions)) {
    return totalParticipants.value;
  }

  return quantities.value.reduce((total, quantity) => {
    const option = selectedAvailability.value.priceOptions.find(
      (priceOption) => normalizeLabel(priceOption?.label) === normalizeLabel(quantity?.optionLabel)
    );

    return total + Number(option?.seatsUsed ?? 1) * Number(quantity?.value ?? 0);
  }, 0);
});

const validationMessage = computed(() => {
  if (!props.item) {
    return "This booking item can’t be moved right now.";
  }

  if (props.bookingHasPastItems) {
    return "This booking can’t be changed because one or more booking times are already in the past.";
  }

  if (!sourceAttraction.value) {
    return "We couldn’t match this booking to an available experience.";
  }

  if (!moveBookingsEnabled.value) {
    return "This booking can’t be moved online.";
  }

  if (bookingIsWithinMoveCutoff.value) {
    return "Your current session start time is too soon to be moved online. Please contact our team on (08) 9757 7411 to discuss your options.";
  }

  if (!selectedAttraction.value || !selectedProductCode.value) {
    return "Choose an experience to continue.";
  }

  if (!selectedTime.value || !selectedAvailability.value?.startTimeLocal) {
    return "Choose a new date and time to continue.";
  }

  if (!selectionChanged.value) {
    return "Choose a different experience, date or time to move this booking.";
  }

  if (bookingHasDistanceConflict.value) {
    return "This selection is too close to another attraction in your booking. Please allow more time for travel between activities.";
  }

  const priceOptions = selectedAvailability.value?.priceOptions ?? [];
  for (const quantity of quantities.value) {
    const priceOption = priceOptions.find(
      (current) => normalizeLabel(current?.label) === normalizeLabel(quantity?.optionLabel)
    );

    if (!priceOption) {
      return `This session doesn’t support your current ${quantity.optionLabel} tickets.`;
    }
  }

  if (Number(selectedAvailability.value?.seatsAvailable ?? 0) < seatsRequired.value) {
    return `This session only has ${selectedAvailability.value?.seatsAvailable ?? 0} seats available for your booking.`;
  }

  return "";
});

const selection = computed(() => ({
  item: props.item,
  itemInd: Number(props.itemInd),
  attraction: selectedAttraction.value,
  productCode: selectedProductCode.value,
  time: selectedTime.value,
  availability: selectedAvailability.value,
}));

const close = () => {
  if (!props.saving && !props.quoting) {
    emit("close");
  }
};

const requestQuote = () => {
  if (!validationMessage.value) {
    emit("quote", selection.value);
  }
};

const confirm = () => {
  if (props.quote && props.quote.payment_action !== "charge") {
    emit("confirm", selection.value);
  }
};

const confirmWithPayment = (confirmationToken) => {
  emit("confirm", {
    ...selection.value,
    confirmationTokenId: confirmationToken?.id,
  });
};

const resetQuote = () => {
  if (!props.saving) {
    emit("reset-quote");
  }
};

const resetSelection = () => {
  selectedTime.value = "";
  selectedAvailability.value = {};
  selectedPrices.value = [];
  emit("reset-quote");
};

const selectAttraction = (event) => {
  selectedAttractionId.value = Number(event.target.value);
  selectedProductCode.value = productOptions.value[0]?.product
    ?? selectedAttraction.value?.rezdy_retail_product_code
    ?? "";
  resetSelection();
};

const selectProduct = (productCode) => {
  selectedProductCode.value = productCode;
  resetSelection();
};

const handleServerResponse = (data) => payment.value?.handleServerResponse(data);

watch(
  () => props.item,
  (item) => {
    const currentAttraction = attractionFromProductCode(attractions.value, item?.productCode);
    const currentTimeValue = item?.startTimeLocal ?? item?.startTime ?? "";

    selectedAttractionId.value = currentAttraction?.id ?? null;
    selectedProductCode.value = item?.productCode ?? currentAttraction?.rezdy_retail_product_code ?? "";
    selectedTime.value = currentTimeValue;
    selectedAvailability.value = {};
    selectedPrices.value = [];
  },
  { immediate: true }
);

defineExpose({
  handleServerResponse,
});
</script>

<template>
  <div class="w-full pb-8 text-dark lg:pb-12">
    <div class="mb-8 flex flex-col gap-5 lg:mb-12">
      <button
        type="button"
        class="inline-flex w-fit items-center gap-2 text-[16px] font-semibold text-dark"
        :disabled="saving || quoting"
        @click="close"
      >
        <span class="text-lg leading-none">‹</span>
        <span class="underline underline-offset-4">Go back</span>
      </button>

      <div class="flex items-start gap-4">
        <span class="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-light lg:h-16 lg:w-16">
          <TicketIcon class="h-6 w-6 lg:h-8 lg:w-8" />
        </span>
        <div>
          <h2 class="text-2xl font-semibold lg:text-4xl">Move booking</h2>
          <p class="mt-2 max-w-2xl text-base text-dark/70 lg:mt-3 lg:text-lg">
            Choose another experience, date or time. We’ll calculate any refund or additional payment before making the change.
          </p>
        </div>
      </div>
    </div>

    <div class="grid gap-10 lg:items-start lg:gap-16 lg:grid-cols-[minmax(0,1fr)_320px]">
      <main class="min-w-0">
        <section v-if="!quote" class="rounded-[28px] bg-light px-5 py-7 lg:px-8 lg:py-8">
          <h3 class="mb-7 text-2xl font-semibold">Choose the new booking</h3>

          <label class="mb-2 block font-bold" for="move-attraction">Experience</label>
          <select
            id="move-attraction"
            class="mb-6 w-full rounded-full border border-white bg-white px-5 py-3"
            :value="selectedAttractionId"
            :disabled="saving || quoting"
            @change="selectAttraction"
          >
            <option
              v-for="availableAttraction in availableAttractions"
              :key="availableAttraction.id"
              :value="availableAttraction.id"
            >
              {{ availableAttraction.retail_title ?? availableAttraction.title }}
            </option>
          </select>

          <fieldset v-if="productOptions.length > 1" class="mb-6">
            <legend class="mb-2 font-bold">Experience option</legend>
            <label
              v-for="productOption in productOptions"
              :key="productOption.product"
              class="mb-2 flex cursor-pointer items-start gap-3 rounded-2xl bg-white px-4 py-3"
            >
              <input
                type="radio"
                class="mt-1"
                name="move-product-option"
                :value="productOption.product"
                :checked="selectedProductCode === productOption.product"
                @change="selectProduct(productOption.product)"
              >
              <span>
                <strong class="block">{{ productOption.title }}</strong>
                <span v-if="productOption.desc" class="text-sm text-dark/70">{{ productOption.desc }}</span>
              </span>
            </label>
          </fieldset>

          <Session
            v-if="selectedAttraction"
            :key="`${selectedAttraction.id}-${selectedProductCode}`"
            v-model="selectedTime"
            v-model:availability="selectedAvailability"
            v-model:prices="selectedPrices"
            :attraction="selectedAttraction"
            :product-code="selectedProductCode || false"
            :submitted="true"
            :hideNumber="true"
          />

          <AlertError v-else>
            Choose an available experience to continue.
          </AlertError>
        </section>

        <div v-else class="space-y-6">
          <section class="rounded-[28px] bg-light px-5 py-7 lg:px-8 lg:py-8">
            <div class="flex items-start justify-between gap-4 border-b border-dashed border-primary pb-5">
              <h3 class="text-2xl font-semibold">Review your move</h3>
              <button type="button" class="font-semibold underline underline-offset-4" :disabled="saving" @click="resetQuote">
                Change selection
              </button>
            </div>

            <div class="mt-5 space-y-4 text-lg">
              <div>
                <div class="text-sm text-dark/50">New experience</div>
                <div class="font-semibold">{{ selectedAttractionTitle }}</div>
              </div>
              <div>
                <div class="text-sm text-dark/50">New date and time</div>
                <div class="font-semibold">{{ selectedTimeLabel }}</div>
              </div>
            </div>
          </section>

          <BookingMovePayment
            v-if="quote.payment_action === 'charge'"
            ref="payment"
            :amount-cents="Number(quote.difference_cents ?? 0)"
            :config="options.gateway ?? {}"
            :customer="booking?.customer ?? {}"
            :saving="saving"
            @payment="confirmWithPayment"
          />
        </div>
      </main>

      <aside class="relative rounded-[32px] bg-light px-6 py-6 lg:sticky lg:top-24">
        <span class="absolute left-4 -top-6 hidden h-12 w-12 items-center justify-center rounded-full bg-primary leading-none text-light lg:inline-flex">
          <TicketIcon />
        </span>

        <h3 class="mt-4 flex items-center gap-3 text-[18px] font-medium lg:block lg:text-2xl">
          <TicketIcon class="lg:hidden" />
          Booking Summary
        </h3>

        <div class="mt-6 border-t border-dashed border-primary pt-5">
          <div class="text-[18px] font-medium">{{ summaryTitle }}</div>
          <div class="mt-1 text-[15px] text-dark/60">{{ summaryTime }}</div>

          <ul class="mt-5 space-y-3">
            <li
              v-for="quantity in summaryQuantities"
              :key="`${item?.id}-${quantity.optionLabel}-summary`"
              class="flex items-center justify-between gap-4 text-[15px]"
            >
              <span>{{ `${quantity.value}x ${quantity.optionLabel}` }}</span>
              <span v-if="summaryTotal !== null">${{ quantity.total.toFixed(2) }}</span>
            </li>
          </ul>
        </div>

        <div class="mt-6 flex items-start justify-between gap-4 border-t border-dashed border-primary pt-5">
          <div class="text-[18px] font-medium">{{ quote ? "New total" : "Total" }}</div>
          <div class="text-right">
            <div v-if="summaryTotal !== null" class="text-xl font-medium">${{ summaryTotal.toFixed(2) }}</div>
            <div v-else class="text-base font-medium">To be calculated</div>
            <div class="mt-1 text-sm text-dark/60">AUD (inc. GST)</div>
          </div>
        </div>

        <template v-if="quote">
          <div class="mt-5 space-y-3 border-t border-dashed border-primary pt-5 text-[15px]">
            <div class="flex items-center justify-between gap-4">
              <span>Current booking total</span>
              <strong>${{ Number(quote.current_total ?? 0).toFixed(2) }}</strong>
            </div>
            <div v-if="quote.payment_action === 'refund'" class="flex items-center justify-between gap-4 text-[#0F5138]">
              <span>Refund</span>
              <strong>-${{ Math.abs(Number(quote.difference ?? 0)).toFixed(2) }}</strong>
            </div>
            <div v-else-if="quote.payment_action === 'charge'" class="flex items-center justify-between gap-4">
              <span>Additional payment</span>
              <strong>${{ Number(quote.difference ?? 0).toFixed(2) }}</strong>
            </div>
            <div v-else class="font-medium text-[#0F5138]">
              There is no price change for this move.
            </div>
          </div>
        </template>

        <AlertError v-if="error" class="mt-5">{{ error }}</AlertError>
        <AlertError v-else-if="!quote && validationMessage" class="mt-5">{{ validationMessage }}</AlertError>

        <button
          v-if="!quote"
          type="button"
          class="mt-6 inline-flex w-full items-center justify-center rounded-full bg-primary px-6 py-4 font-semibold text-dark disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="saving || quoting || !!validationMessage"
          @click="requestQuote"
        >
          {{ quoting ? "Calculating..." : "Review move" }}
        </button>

        <button
          v-else-if="quote.payment_action !== 'charge'"
          type="button"
          class="mt-6 inline-flex w-full items-center justify-center rounded-full bg-primary px-6 py-4 font-semibold text-dark disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="saving"
          @click="confirm"
        >
          {{ saving ? "Moving booking..." : "Confirm move" }}
        </button>

        <p v-if="quote?.payment_action === 'refund'" class="mt-3 text-sm text-dark/60">
          The refund will be returned to the original payment method.
        </p>
        <p v-else-if="quote?.payment_action === 'charge'" class="mt-3 text-sm text-dark/60">
          Complete the secure payment form to confirm this move.
        </p>

        <button
          type="button"
          class="mt-5 w-full text-center text-[15px] font-semibold underline underline-offset-4"
          :disabled="saving || quoting"
          @click="close"
        >
          Cancel move
        </button>
      </aside>
    </div>
  </div>
</template>
